import { getFormTitle } from '@/common/utils/formTitle'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export type DocumentTabType = 'list' | 'document'

export type DocumentTabRouteName = 'platform' | 'document'

export interface DocumentTab {
  id: string
  title: string
  type: DocumentTabType
  closable: boolean
  /** Маршрут для router.push при фокусе вкладки */
  routeName?: DocumentTabRouteName
}

type RegisterPlatformRootTabMode = 'replace-current' | 'append'

const TAB_TITLE_LOADING = 'Загрузка...'

/** Пока форма/данные не дали заголовок — не показываем «Список 2052», а единый текст загрузки */
function resolveInitialTabTitle(titleFromPageData: string | undefined): string {
  const t = titleFromPageData?.trim()
  if (t) return t
  return TAB_TITLE_LOADING
}

export const useDocumentTabsStore = defineStore('documentTabs', () => {
  const tabs = ref<DocumentTab[]>([])
  const rootTabId = ref<string | null>(null)

  const tabIdsOrdered = computed(() => tabs.value.map((t) => t.id))

  const idsForPageDataPreserve = computed(() => new Set(tabs.value.map((t) => t.id)))

  function isTabClosable(tab: DocumentTab | undefined | null): boolean {
    return Boolean(tab)
  }

  function normalizeListTabsClosable() {
    if (!tabs.value.some((t) => t.type === 'list' && !t.closable)) {
      return
    }
    tabs.value = tabs.value.map((t) =>
      t.type === 'list' && !t.closable
        ? { ...t, closable: true, routeName: t.routeName ?? 'platform' }
        : t,
    )
  }

  function upsertTab(entry: DocumentTab) {
    normalizeListTabsClosable()
    const i = tabs.value.findIndex((t) => t.id === entry.id)
    if (i === -1) {
      tabs.value = [...tabs.value, { ...entry }]
      return
    }
    const next = [...tabs.value]
    next[i] = { ...next[i], ...entry }
    tabs.value = next
  }

  function refreshRootFallback(preferredId?: string | null) {
    normalizeListTabsClosable()
    const preferred = preferredId != null ? String(preferredId) : ''
    if (preferred) {
      const hit = tabs.value.find((t) => t.id === preferred && t.type === 'list')
      if (hit) {
        rootTabId.value = hit.id
        return
      }
    }
    const lastList = [...tabs.value].reverse().find((t) => t.type === 'list')
    rootTabId.value = lastList?.id ?? null
  }

  function registerPlatformRootTab(
    platformId: string,
    pageData?: Record<string, unknown> | null,
    mode: RegisterPlatformRootTabMode = 'replace-current',
  ) {
    const prevRootId = rootTabId.value
    rootTabId.value = platformId
    const title = pageData ? getFormTitle(pageData) : ''
    const rootEntry: DocumentTab = {
      id: platformId,
      title: resolveInitialTabTitle(title),
      type: 'list',
      closable: true,
      routeName: 'platform',
    }
    const existingIndex = tabs.value.findIndex((t) => t.id === platformId)
    if (existingIndex !== -1) {
      upsertTab(rootEntry)
      return
    }
    if (mode === 'append') {
      upsertTab(rootEntry)
      return
    }
    const replaceIndex = prevRootId != null
      ? tabs.value.findIndex((t) => t.id === prevRootId && t.type === 'list')
      : -1
    if (replaceIndex === -1) {
      upsertTab(rootEntry)
      return
    }
    const next = [...tabs.value]
    next[replaceIndex] = rootEntry
    tabs.value = next
  }

  /**
   * Открытие или фокус вкладки документа (NEW_WINDOW / клик по уже открытому).
   */
  function openOrFocusDocumentTab(
    id: string,
    pageData?: Record<string, unknown> | null,
    routeName?: DocumentTabRouteName,
  ) {
    const title = pageData ? getFormTitle(pageData) : ''
    const existing = tabs.value.findIndex((t) => t.id === id)
    if (existing !== -1) {
      const prev = tabs.value[existing]
      const next = [...tabs.value]
      next[existing] = {
        ...prev,
        type: 'document',
        title: title || prev.title,
        routeName: routeName !== undefined && routeName !== null ? routeName : prev.routeName,
      }
      tabs.value = next
      return
    }
    upsertTab({
      id,
      title: resolveInitialTabTitle(title),
      type: 'document',
      closable: true,
      routeName: routeName ?? 'document',
    })
  }

  function updateTitleFromPageData(id: string, pageData: Record<string, unknown> | null | undefined) {
    normalizeListTabsClosable()
    if (!pageData) return
    const nt = getFormTitle(pageData)
    if (!nt) return
    const i = tabs.value.findIndex((t) => t.id === id)
    if (i === -1) return
    const next = [...tabs.value]
    next[i] = { ...next[i], title: nt }
    tabs.value = next
  }

  /**
   * Закрыть вкладку.
   */
  function removeTabEntry(id: string): { ok: true } | { ok: false; reason: 'root' | 'missing' | 'pinned' } {
    normalizeListTabsClosable()
    const tab = tabs.value.find((t) => t.id === id)
    if (!tab) return { ok: false, reason: 'missing' }
    tabs.value = tabs.value.filter((t) => t.id !== id)
    if (rootTabId.value === id || tab.type === 'list') {
      refreshRootFallback(rootTabId.value === id ? null : rootTabId.value)
    }
    return { ok: true }
  }

  function isTabOpen(id: string): boolean {
    normalizeListTabsClosable()
    return tabs.value.some((t) => t.id === id)
  }

  function resetWorkbenchTabs() {
    tabs.value = []
    rootTabId.value = null
  }

  /** Закрыть все вкладки кроме anchorId (корень и прочие не-closable не трогаем). Возвращает id закрытых. */
  function closeOtherTabsExcept(anchorId: string): string[] {
    normalizeListTabsClosable()
    const toClose = tabs.value.filter((t) => t.id !== anchorId && isTabClosable(t)).map((t) => t.id)
    for (const id of toClose) {
      removeTabEntry(id)
    }
    return toClose
  }

  /** Закрыть closable-вкладки строго правее anchorId. Возвращает id закрытых. */
  function closeTabsToTheRightOf(anchorId: string): string[] {
    normalizeListTabsClosable()
    const idx = tabs.value.findIndex((t) => t.id === anchorId)
    if (idx === -1) return []
    const toClose = tabs.value
      .slice(idx + 1)
      .filter((t) => isTabClosable(t))
      .map((t) => t.id)
    for (const id of toClose) {
      removeTabEntry(id)
    }
    return toClose
  }

  return {
    tabs,
    rootTabId,
    tabIdsOrdered,
    idsForPageDataPreserve,
    isTabClosable,
    registerPlatformRootTab,
    openOrFocusDocumentTab,
    updateTitleFromPageData,
    removeTabEntry,
    isTabOpen,
    resetWorkbenchTabs,
    closeOtherTabsExcept,
    closeTabsToTheRightOf,
  }
})
